/**
 * Exec band tiles (quick-261104-ia3, John 2026-11-04): "order should be trended
 * ARR, churn, the pipeline trend … then a monitor of major problems tile and a
 * monitor of major changes tile."
 *
 * ARR + churn read the dated Rillet constant (lib/exec/rillet-arr.ts — Rillet is
 * not wired into the sync-worker yet). The funnel, trend and potential-opp tiles
 * read GET /api/main/exec.
 */
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo } from 'react';

import { BAR_RADIUS, INK, POTENTIAL_COLOR, SERIES, STAGE_BANDS, baseOption, stageColor, tipHead, tipNote, tipRow } from '../../lib/exec/chart.js';
import { change, dayLabel, money, monthLabel, num, pct } from '../../lib/exec/fmt.js';
import { YEAR_END_ARR_FORECAST, sfLink } from '../../lib/exec/links.js';
import { useRillet } from '../../lib/exec/rillet-source.js';

import { BloodDrive } from './controls.js';
import { ChangeChip, DashTile, Legend, TileEmpty } from './DashTile.js';

import type { ExecFunnelPanel, ExecFunnelSnapshot } from '@saledash/shared';
import type { EChartsOption } from 'echarts';

const FUNNEL_ORDER = ['Discovery', 'Demo', 'Scoping', 'Business Win', 'Technical Win', 'Confirmed', 'Negotiation'];

// ─── ARR trend ──────────────────────────────────────────────────────────────
export function ArrTrendTile(): React.JSX.Element {
  const R = useRillet();
  const months = R.months.slice(-13);
  const last = months[months.length - 1];
  const yearAgo = R.months[R.months.length - 13];
  const yoy = last && yearAgo ? change(last.arr, yearAgo.arr) : null;

  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true, left: 48 });
    return {
      ...base,
      grid: { left: 48, right: 8, top: 10, bottom: 24 },
      xAxis: { ...(base.xAxis as object), data: months.map((m) => `${monthLabel(m.month)}${m.month === R.provisionalMonth ? "*" : ""}`), boundaryGap: false },
      yAxis: { ...(base.yAxis as object), min: (v: { min: number }) => Math.floor((v.min * 0.92) / 500_000) * 500_000 },
      tooltip: {
        ...(base.tooltip as object),
        axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
        formatter: (params: unknown) => {
          const p = (params as { dataIndex: number }[])[0];
          const m = p ? months[p.dataIndex] : undefined;
          if (!m) return '';
          const provisional = m.month === R.provisionalMonth;
          return (
            tipHead(`${monthLabel(m.month)} · ARR ${money(m.arr)}`) +
            tipRow(SERIES.aqua, 'New customers', money(m.newArr)) +
            tipRow(SERIES.blue, 'Growth from existing', money(m.expansionArr)) +
            tipRow(SERIES.red, 'Lost (churn + shrink)', money(m.churnArr + m.contractionArr)) +
            tipRow(null, 'Paying customers', num(m.customers)) +
            tipNote(
              provisional
                ? 'Latest month is provisional: renewals not yet booked in Rillet show as lost until they are.'
                : 'Annual recurring revenue on the last day of the month, from Rillet.',
            )
          );
        },
      },
      series: [
        {
          type: 'line',
          data: months.map((m) => m.arr),
          smooth: 0.2,
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: false,
          lineStyle: { width: 2, color: SERIES.blue },
          itemStyle: { color: SERIES.blue },
          areaStyle: { color: 'rgba(42,120,214,0.10)' },
        },
      ],
    };
  }, [months, R.provisionalMonth]);

  return (
    <DashTile
      title="ARR trend"
      info="Annual recurring revenue at each month-end, from Rillet. The bar on the right fills toward this year's year-end ARR forecast. Hover a month to see what was added and what was lost."
      headline={last ? money(last.arr) : '—'}
      sub={
        <span className="inline-flex items-center gap-1.5">
          {yoy ? <ChangeChip text={yoy.text} dir={yoy.dir} suffix="vs a year ago" /> : null}
          <span>{last ? `${num(last.customers)} paying customers` : ''}</span>
        </span>
      }
      size="lg"
      footer={`Rillet ARR waterfall · pulled ${R.pulledOn} · ${monthLabel(R.provisionalMonth)} provisional`}
      testId="exec-arr"
    >
      <div className="flex h-full gap-3">
        <div className="min-w-0 flex-1">
          <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
        </div>
        <div className="flex w-24 shrink-0 items-end">
          <BloodDrive
            label="Year-end ARR"
            value={last?.arr ?? 0}
            target={YEAR_END_ARR_FORECAST}
            targetNote="2027 plan forecast"
            height={190}
          />
        </div>
      </div>
    </DashTile>
  );
}

// ─── Churn ──────────────────────────────────────────────────────────────────
export function ChurnTile(): React.JSX.Element {
  const R = useRillet();
  const months = R.months.slice(-12);
  const lost12 = months.reduce((s, m) => s + m.churnArr, 0);
  const shrink12 = months.reduce((s, m) => s + m.contractionArr, 0);
  const startArr = R.months[R.months.length - 13]?.arr ?? null;
  const logos12 = months.reduce((s, m) => s + m.churnedLogos, 0);

  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true, left: 44 });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: months.map((m) => `${monthLabel(m.month)}${m.month === R.provisionalMonth ? "*" : ""}`) },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const p = (params as { dataIndex: number }[])[0];
          const m = p ? months[p.dataIndex] : undefined;
          if (!m) return '';
          const names = m.churned
            .slice(0, 4)
            .map((c) => tipRow(null, c.name, money(c.arr)))
            .join('');
          const provisional = m.month === R.provisionalMonth;
          return (
            tipHead(`${monthLabel(m.month)} · lost ${money(m.churnArr + m.contractionArr)}`) +
            tipRow(SERIES.red, 'Customers who left', money(m.churnArr)) +
            tipRow('#f2a3a2', 'Customers who shrank', money(m.contractionArr)) +
            names +
            (m.churned.length > 4 ? tipNote(`+${String(m.churned.length - 4)} more`) : '') +
            (provisional ? tipNote('Provisional: some of these may be renewals not yet booked in Rillet.') : '')
          );
        },
      },
      series: [
        {
          name: 'Left',
          type: 'bar',
          stack: 'lost',
          barMaxWidth: 22,
          data: months.map((m) => ({
            value: m.churnArr,
            itemStyle:
              m.month === R.provisionalMonth
                ? { color: SERIES.red, borderColor: '#7f1d1d', borderWidth: 1.5, borderType: 'dashed' as const }
                : { color: SERIES.red },
          })),
          itemStyle: { borderColor: '#fff', borderWidth: 1 },
        },
        {
          name: 'Shrank',
          type: 'bar',
          stack: 'lost',
          barMaxWidth: 22,
          data: months.map((m) => m.contractionArr),
          itemStyle: { color: '#f2a3a2', borderRadius: BAR_RADIUS, borderColor: '#fff', borderWidth: 1 },
        },
      ],
    };
  }, [months, R.provisionalMonth]);

  return (
    <DashTile
      title="Churn"
      info="Recurring revenue we lost each month: customers who left, plus customers who renewed for less. Taller bars are worse. The faded bar is the latest month, which may still change as renewals get booked."
      headline={money(lost12 + shrink12)}
      sub={`lost in 12 months · ${startArr ? pct((lost12 + shrink12) / startArr) : '—'} of ARR · ${num(logos12)} customers left`}
      size="lg"
      footer={`Rillet · excludes one-month booking gaps · * provisional · pulled ${R.pulledOn}`}
      testId="exec-churn"
    >
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1">
          <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
        </div>
        <Legend
          items={[
            { label: 'Customers who left', color: SERIES.red },
            { label: 'Customers who shrank', color: '#f2a3a2' },
          ]}
        />
      </div>
    </DashTile>
  );
}

// ─── Funnel by amount ───────────────────────────────────────────────────────
export function FunnelTile({
  funnel,
  avgDeal,
  loading,
  onAuditPotential,
}: {
  funnel: ExecFunnelPanel | undefined;
  avgDeal: number | null;
  loading: boolean;
  /** Opens the potential-opps audit list (click the orange bar or the button). */
  onAuditPotential?: () => void;
}): React.JSX.Element {
  const rows = useMemo(() => {
    if (!funnel) return [];
    const potentialAmt = avgDeal != null ? funnel.potential.length * avgDeal : 0;
    return [
      { stage: 'Potential opp', amount: potentialAmt, count: funnel.potential.length, color: POTENTIAL_COLOR },
      ...FUNNEL_ORDER.map((s) => ({
        stage: s,
        amount: funnel.current.byStage[s] ?? 0,
        count: funnel.current.countByStage[s] ?? 0,
        color: stageColor(s),
      })),
    ];
  }, [funnel, avgDeal]);
  const openTotal = rows.slice(1).reduce((s, r) => s + r.amount, 0);
  const openCount = rows.slice(1).reduce((s, r) => s + r.count, 0);

  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    return {
      ...base,
      grid: { left: 96, right: 56, top: 4, bottom: 4 },
      xAxis: { type: 'value', show: false, max: (v: { max: number }) => v.max * 1.05 },
      yAxis: {
        type: 'category',
        inverse: true,
        data: rows.map((r) => r.stage),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: INK.secondary, fontSize: 11 },
      },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const p = (params as { dataIndex: number }[])[0];
          const r = p ? rows[p.dataIndex] : undefined;
          if (!r) return '';
          if (r.stage === 'Potential opp') {
            return (
              tipHead(`Potential opps · ${money(r.amount)}`) +
              tipRow(r.color, 'Companies', num(r.count)) +
              tipRow(null, 'Valued at avg deal', money(avgDeal)) +
              tipNote('Met with us, no opportunity yet, still active in the last 60 days. Not pipeline yet; it is the pool that should become pipeline. Click the bar to audit the list.')
            );
          }
          return (
            tipHead(`${r.stage} · ${money(r.amount)}`) +
            tipRow(r.color, 'Open deals', num(r.count)) +
            tipRow(null, 'Avg deal here', r.count ? money(r.amount / r.count) : '—') +
            tipNote('Open product deals sitting in this stage today.')
          );
        },
      },
      series: [
        {
          type: 'bar',
          barWidth: '62%',
          data: rows.map((r) => ({ value: r.amount, itemStyle: { color: r.color, borderRadius: [0, 4, 4, 0] } })),
          label: {
            show: true,
            position: 'right',
            color: INK.secondary,
            fontSize: 10,
            formatter: (p: unknown) => money((p as { value: number }).value),
          },
        },
      ],
    };
  }, [rows, avgDeal]);

  return (
    <DashTile
      title="Pipeline funnel"
      info="Every open product deal by stage, in dollars, with the potential opps on top: companies that met with us and are still engaged but have no opportunity yet. Read it top to bottom; deals should flow down toward a close."
      headline={funnel ? money(openTotal) : '—'}
      sub={funnel ? `open in ${num(openCount)} deals · + ${money(rows[0]?.amount ?? 0)} potential` : ''}
      size="lg"
      control={
        funnel && onAuditPotential ? (
          <button
            type="button"
            onClick={onAuditPotential}
            className="rounded-md border border-ink-200 bg-white px-2 py-0.5 text-[0.68rem] font-medium text-brand-600 hover:border-brand-300"
            data-testid="audit-potential"
          >
            Audit potential ({num(funnel.potential.length)})
          </button>
        ) : undefined
      }
      footer="Salesforce · Product record type · potential valued at the avg closed-won deal"
      testId="exec-funnel"
    >
      {!funnel ? (
        <TileEmpty text={loading ? 'Loading…' : 'Funnel data appears after the next refresh cycle.'} />
      ) : (
        <ReactECharts
          option={option}
          style={{ height: '100%', width: '100%', cursor: 'default' }}
          notMerge
          onEvents={{
            click: (p: { dataIndex?: number }) => {
              if (p.dataIndex === 0 && onAuditPotential) onAuditPotential();
            },
          }}
        />
      )}
    </DashTile>
  );
}

// ─── Funnel over time (stacked area) ────────────────────────────────────────
function bandValue(s: ExecFunnelSnapshot, stages: string[]): number {
  return stages.reduce((sum, st) => sum + (s.byStage[st] ?? 0), 0);
}

export function FunnelTrendTile({
  funnel,
  avgDeal,
  loading,
}: {
  funnel: ExecFunnelPanel | undefined;
  avgDeal: number | null;
  loading: boolean;
}): React.JSX.Element {
  const points = useMemo(() => (funnel ? [...funnel.history, { ...funnel.current, potentialCount: funnel.potential.length }] : []), [funnel]);
  const totals = points.map((p) => STAGE_BANDS.reduce((s, b) => s + bandValue(p, b.stages), 0));
  const first = totals[0];
  const lastTotal = totals[totals.length - 1];
  const delta = first != null && lastTotal != null ? change(lastTotal, first) : null;

  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true, left: 48 });
    const labels = points.map((p, i) => (i === points.length - 1 ? 'Now' : monthLabel(p.asOf.slice(0, 7))));
    const potential = points.map((p) => (avgDeal != null ? p.potentialCount * avgDeal : 0));
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: labels, boundaryGap: false },
      tooltip: {
        ...(base.tooltip as object),
        axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
        formatter: (params: unknown) => {
          const i = (params as { dataIndex: number }[])[0]?.dataIndex ?? 0;
          const p = points[i];
          if (!p) return '';
          const rowsHtml = FUNNEL_ORDER.slice()
            .reverse()
            .filter((st) => (p.byStage[st] ?? 0) > 0)
            .map((st) => tipRow(stageColor(st), st, money(p.byStage[st] ?? 0)))
            .join('');
          return (
            tipHead(`${i === points.length - 1 ? 'Today' : `End of ${monthLabel(p.asOf.slice(0, 7))}`} · ${money(totals[i] ?? 0)} open`) +
            tipRow(POTENTIAL_COLOR, `Potential (${String(p.potentialCount)} cos.)`, money(potential[i] ?? 0)) +
            rowsHtml +
            tipNote('Growing bands = more pipeline in that stage. Shrinking = deals closed, slipped out or were lost.')
          );
        },
      },
      series: [
        ...STAGE_BANDS.slice()
          .reverse()
          .map((b) => ({
            name: b.label,
            type: 'line' as const,
            stack: 'funnel',
            symbol: 'none',
            smooth: 0.15,
            lineStyle: { width: 1, color: '#ffffff' },
            areaStyle: { color: b.color, opacity: 1 },
            data: points.map((p) => bandValue(p, b.stages)),
          })),
        {
          name: 'Potential',
          type: 'line' as const,
          stack: 'funnel',
          symbol: 'none',
          smooth: 0.15,
          lineStyle: { width: 1, color: '#ffffff' },
          areaStyle: { color: POTENTIAL_COLOR, opacity: 0.55 },
          data: potential,
        },
      ],
    };
  }, [points, totals, avgDeal]);

  return (
    <DashTile
      title="Pipeline trend by stage"
      info="The same funnel at every month-end, stacked: each colored band is the dollars in one stage, orange on top is potential opps. Bands thickening means growth; bands thinning means deals left that stage (won, lost or slipped)."
      headline={lastTotal != null ? money(lastTotal) : '—'}
      sub={
        delta ? (
          <ChangeChip text={delta.text} dir={delta.dir} suffix={`since ${points[0] ? monthLabel(points[0].asOf.slice(0, 7)) : ''}`} />
        ) : (
          ''
        )
      }
      size="lg"
      footer="Rebuilt from Salesforce opportunity history at each month-end · potential × avg deal"
      testId="exec-funnel-trend"
    >
      {!funnel ? (
        <TileEmpty text={loading ? 'Loading…' : 'Trend appears after the next refresh cycle.'} />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend
            items={[
              { label: 'Potential', color: POTENTIAL_COLOR },
              ...STAGE_BANDS.map((b) => ({ label: b.label, color: b.color })),
            ]}
          />
        </div>
      )}
    </DashTile>
  );
}

// ─── Potential opps list ────────────────────────────────────────────────────
export function PotentialOppsTile({
  funnel,
  avgDeal,
  loading,
}: {
  funnel: ExecFunnelPanel | undefined;
  avgDeal: number | null;
  loading: boolean;
}): React.JSX.Element {
  const list = funnel?.potential ?? [];
  return (
    <DashTile
      title="Potential opps"
      info={funnel?.potentialRule ?? 'Companies that met with us and are still active but have no opportunity yet.'}
      headline={funnel ? money(avgDeal != null ? list.length * avgDeal : null) : '—'}
      sub={funnel ? `${num(list.length)} companies × ${money(avgDeal)} avg deal` : ''}
      size="xl"
      footer="Each row is one company · newest meeting first · click to open in Salesforce"
      testId="exec-potential"
    >
      {!funnel ? (
        <TileEmpty text={loading ? 'Loading…' : 'The list appears after the next refresh cycle.'} />
      ) : list.length === 0 ? (
        <TileEmpty text="No held meetings are waiting on an opportunity right now." />
      ) : (
        <div className="h-full overflow-y-auto pr-1">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-white text-[0.66rem] uppercase tracking-wide text-ink-400">
              <tr>
                <th className="py-1 font-medium">Company</th>
                <th className="py-1 font-medium">Owner</th>
                <th className="py-1 text-right font-medium">Met</th>
                <th className="py-1 text-right font-medium">Active</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-t border-ink-100">
                  <td className="max-w-[10rem] truncate py-1.5 pr-2">
                    <a href={sfLink(p.id)} className="text-brand-600 hover:underline" title={p.name}>
                      {p.name}
                    </a>
                    {p.kind === 'lead' ? <span className="ml-1 text-[0.6rem] text-ink-400">lead</span> : null}
                  </td>
                  <td className="max-w-[6rem] truncate py-1.5 pr-2 text-ink-600">{p.owner ?? '—'}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-600">{dayLabel(p.lastMeeting)}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-600">{p.lastActivity ? dayLabel(p.lastActivity) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashTile>
  );
}

// ─── Potential opps audit dialog (opened on demand) ─────────────────────────
export function PotentialOppsDialog({
  open,
  onClose,
  funnel,
  avgDeal,
}: {
  open: boolean;
  onClose: () => void;
  funnel: ExecFunnelPanel | undefined;
  avgDeal: number | null;
}): React.JSX.Element | null {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Potential opps audit"
      onClick={onClose}
      data-testid="potential-dialog"
    >
      <div
        className="relative w-full max-w-3xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-md border border-ink-200 bg-white px-2 py-0.5 text-xs text-ink-600 hover:border-brand-300"
        >
          Close ✕
        </button>
        <PotentialOppsTile funnel={funnel} avgDeal={avgDeal} loading={false} />
      </div>
    </div>
  );
}
